import type { TechRadarApi } from '@backstage-community/plugin-tech-radar';
import {
  MovedState,
} from '@backstage-community/plugin-tech-radar-common';
import type {
  TechRadarLoaderResponse,
  RadarRing,
  RadarQuadrant,
  RadarEntry,
} from '@backstage-community/plugin-tech-radar-common';

const rings: RadarRing[] = [
  { id: 'adopt',  name: 'Adopt',  color: '#0052cc' },
  { id: 'trial',  name: 'Trial',  color: '#0ea5e9' },
  { id: 'assess', name: 'Assess', color: '#f59e0b' },
  { id: 'hold',   name: 'Hold',   color: '#ef4444' },
];

const quadrants: RadarQuadrant[] = [
  { id: 'languages',  name: 'Languages & Frameworks' },
  { id: 'platforms',  name: 'Platforms & Infrastructure' },
  { id: 'tools',      name: 'Tools' },
  { id: 'techniques', name: 'Techniques' },
];

// Edit entries below to reflect your organisation's tech landscape.
// Each entry needs: key (unique), id (unique), quadrant, title, and at least one timeline item.
const entries: RadarEntry[] = [
  // ── Languages & Frameworks ─────────────────────────────────────────────────
  {
    key: 'dotnet',
    id: 'dotnet',
    quadrant: 'languages',
    title: '.NET / C#',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Primary backend language' }],
  },
  {
    key: 'java',
    id: 'java',
    quadrant: 'languages',
    title: 'Java',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Used across several services' }],
  },
  {
    key: 'typescript',
    id: 'typescript',
    quadrant: 'languages',
    title: 'TypeScript',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Frontend and tooling standard' }],
  },
  {
    key: 'react',
    id: 'react',
    quadrant: 'languages',
    title: 'React',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'UI framework of choice' }],
  },
  {
    key: 'python',
    id: 'python',
    quadrant: 'languages',
    title: 'Python',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'trial', moved: MovedState.NoChange, description: 'Used for scripting and automation' }],
  },

  // ── Platforms & Infrastructure ─────────────────────────────────────────────
  {
    key: 'azure',
    id: 'azure',
    quadrant: 'platforms',
    title: 'Microsoft Azure',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Primary cloud platform' }],
  },
  {
    key: 'docker',
    id: 'docker',
    quadrant: 'platforms',
    title: 'Docker',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Containerisation standard' }],
  },
  {
    key: 'postgresql',
    id: 'postgresql',
    quadrant: 'platforms',
    title: 'PostgreSQL',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Primary relational database' }],
  },
  {
    key: 'kubernetes',
    id: 'kubernetes',
    quadrant: 'platforms',
    title: 'Kubernetes',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'trial', moved: MovedState.Up, description: 'Evaluating for orchestration' }],
  },

  // ── Tools ─────────────────────────────────────────────────────────────────
  {
    key: 'gitlab',
    id: 'gitlab',
    quadrant: 'tools',
    title: 'GitLab',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Source control and CI/CD' }],
  },
  {
    key: 'backstage',
    id: 'backstage',
    quadrant: 'tools',
    title: 'Backstage',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'trial', moved: MovedState.Up, description: 'Internal developer portal' }],
  },
  {
    key: 'sonarqube',
    id: 'sonarqube',
    quadrant: 'tools',
    title: 'SonarQube',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Static code analysis' }],
  },

  // ── Techniques ─────────────────────────────────────────────────────────────
  {
    key: 'rest-api',
    id: 'rest-api',
    quadrant: 'techniques',
    title: 'REST APIs',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Standard API design approach' }],
  },
  {
    key: 'gitflow',
    id: 'gitflow',
    quadrant: 'techniques',
    title: 'GitFlow',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'adopt', moved: MovedState.NoChange, description: 'Branching strategy' }],
  },
  {
    key: 'domain-driven-design',
    id: 'domain-driven-design',
    quadrant: 'techniques',
    title: 'Domain-Driven Design',
    timeline: [{ date: new Date('2024-01-01'), ringId: 'assess', moved: MovedState.NoChange, description: 'Under evaluation for service boundaries' }],
  },
];

export class TechRadarClient implements TechRadarApi {
  async load(_id: string | undefined): Promise<TechRadarLoaderResponse> {
    return { rings, quadrants, entries };
  }
}
